import { GameRouteService } from "./GameRouteService";
import { GameState } from "@shared/types";

/**
 * Service for managing global game state
 */
export class GameStateService {
    private static instance: GameStateService;
    private _isGameStarted: boolean = false;
    private _gameRouteService: GameRouteService = new GameRouteService();
    private _subscribers: Set<(started: boolean) => void> = new Set();

    private constructor() {
        // Initial check
        void this.checkGameState();
    }

    /**
     * Get the singleton instance
     */
    public static getInstance(): GameStateService {
        GameStateService.instance = new GameStateService();
        return GameStateService.instance;
    }

    /**
     * Subscribe to game state changes
     */
    public subscribe(callback: (started: boolean) => void): () => void {
        this._subscribers.add(callback);
        // Immediately call with current state
        callback(this._isGameStarted);
        // Return unsubscribe function
        return () => this._subscribers.delete(callback);
    }

    /**
     * Get current game state
     */
    public get isGameStarted(): boolean {
        return this._isGameStarted;
    }

    /**
     * Check the current game state
     * This should be called whenever a component rerenders
     */
    public async checkGameState(): Promise<void> {
        try {
            const gameState: GameState = await this._gameRouteService.getGameState();
            const isStarted: boolean = gameState.type === "default" ? !!gameState.startedGame : false;

            if (this._isGameStarted !== isStarted) {
                this._isGameStarted = isStarted;
                this.notifySubscribers();
            }
        }
        catch (error) {
            console.error("Failed to fetch game state:", error);
            // Don't update state on error, keep previous state
        }
    }

    /**
     * Notify all subscribers of state change
     */
    private notifySubscribers(): void {
        for (const callback of this._subscribers) {
            try {
                callback(this._isGameStarted);
            }
            catch (error) {
                console.error("Error in game state subscriber:", error);
            }
        }
    }
}
